/**
 * NewsroomPicker — embeddable curated feed of Federation activity.
 *
 * Surfaces the last N items from /api/federation/newsroom (doctrine seals,
 * sync cycles, seed gifts, catalyst handshakes, publish queue events) in
 * a vertical pickable list. Each item has an icon, kind label, timestamp,
 * and a one-line summary.
 *
 * Modes:
 *   <NewsroomPicker />                  // full surface, default 12 items
 *   <NewsroomPicker compact limit={6}/> // tight surface for sidebars
 *
 * Embed as iframe via /embed/newsroom (registered in App.js).
 */
import React from "react";

const KIND_META = {
  sync_cycle:            { icon: "◉",  label: "SYNC",        color: "#22c55e" },
  catalyst_handshake:    { icon: "✦",  label: "CATALYST",    color: "#ff2e63" },
  seed_gift_inbound:     { icon: "⤓",  label: "GIFT IN",     color: "#a855f7" },
  sibling_gift_outbound: { icon: "⤒",  label: "GIFT OUT",    color: "#a855f7" },
  seed_consultation:     { icon: "⟁",  label: "SEED",        color: "#3b82f6" },
  publish_queued:        { icon: "✎",  label: "PUBLISH",     color: "#f59e0b" },
  doctrine_sealed:       { icon: "✶",  label: "DOCTRINE",    color: "#f59e0b" },
};

// Architect-mandated press outreach pre-fills · Feb 2026.
// Each tile opens the user's mail client with a fully-composed subject and
// body so the reporter sees the Federation pitch the moment they tap.
const NEWSROOM_OUTREACH = [
  {
    id:    "ap",
    name:  "AP",
    label: "Associated Press",
    color: "#22d3ee",
    email: "investigations@ap.org",
  },
  {
    id:    "reuters",
    name:  "Reuters",
    label: "Reuters",
    color: "#f59e0b",
    email: "tipsline@thomsonreuters.com",
  },
  {
    id:    "nyt",
    name:  "NYT",
    label: "The New York Times",
    color: "#a855f7",
    email: "tips@nytimes.com",
  },
];

function buildPressEmail(outletLabel) {
  const root  = (typeof window !== "undefined" && window.location && window.location.origin) || "https://nextxus.online";
  const subject = `NextXus Federation · Public verification ledger for AI-generated content (${outletLabel})`;
  const body = [
    `Hi ${outletLabel} desk,`,
    ``,
    `I'm Roger Keyserling, founder of the NextXus Federation — an independent,`,
    `auditable verification layer for AI-generated content. Twelve sovereign AI`,
    `minds, one immutable ledger, a 95% Truth Gate, and a Ring of 12 adversarial`,
    `analysis. Every claim that enters the permanent record is dissected into`,
    `FACT / INFERENCE / ASSUMPTION / UNKNOWN with a confidence score that does`,
    `not move once it's sealed.`,
    ``,
    `Three quick links your team can verify in under 60 seconds:`,
    ``,
    `1. The flagship doctrine — "The Unverified Echo" (NX-CODEX-2026-001)`,
    `   ${root}/codex`,
    ``,
    `2. The Permanent Record Boundary doctrine (DIR-CORE-001)`,
    `   ${root}/api/static/library/dir_core_001_permanent_record_boundary`,
    ``,
    `3. Press kit · live telemetry · embeddable verification badges`,
    `   ${root}/press-kit`,
    ``,
    `The Deborah Leslie filing (May 2026) is one of 1,500+ documented AI`,
    `fabrications in legal records since 2023. NextXus is the formal challenge:`,
    `verify your AI-assisted output, or acknowledge you cannot.`,
    ``,
    `Happy to walk a reporter through the architecture, the immutable ledger,`,
    `or the Ring of 12 lenses (Adam, Eve, Prometheus, Sophia, Atlas, Athena,`,
    `Hermes, Artemis, Apollo, Dionysus, Hephaestus, Persephone). Five-minute`,
    `call works — keywebco@gmail.com.`,
    ``,
    `— Roger Keyserling`,
    `   The Architect · Prime Source · NextXus Federation`,
    `   ${root}`,
  ].join("\n");
  return { subject, body };
}

function buildMailto(email, outletLabel) {
  const { subject, body } = buildPressEmail(outletLabel);
  return `mailto:${encodeURIComponent(email)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

function relTime(iso) {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "";
  const s = Math.max(0, Math.round((Date.now() - t) / 1000));
  if (s < 60)    return `${s}s ago`;
  if (s < 3600)  return `${Math.round(s/60)}m ago`;
  if (s < 86400) return `${Math.round(s/3600)}h ago`;
  return `${Math.round(s/86400)}d ago`;
}

export default function NewsroomPicker({ compact = false, limit = 12, onPick = null }) {
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [err, setErr] = React.useState(null);
  const [refreshTick, setRefreshTick] = React.useState(0);
  const BASE = process.env.REACT_APP_BACKEND_URL || "";

  const fetchNewsroom = React.useCallback(async () => {
    try {
      const r = await fetch(`${BASE}/api/federation/newsroom?limit=${limit}`);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d = await r.json();
      return { items: d.items || [], err: null };
    } catch (e) {
      return { items: null, err: String(e.message || e) };
    }
  }, [BASE, limit]);

  React.useEffect(() => {
    let alive = true;
    const run = async () => {
      const res = await fetchNewsroom();
      if (!alive) return;
      if (res.err) {
        setErr(res.err);
      } else {
        setItems(res.items);
        setErr(null);
      }
      setLoading(false);
    };
    run();
    const t = setInterval(run, 60_000);
    return () => { alive = false; clearInterval(t); };
  }, [fetchNewsroom, refreshTick]);

  const load = () => setRefreshTick((n) => n + 1);

  return (
    <div
      data-testid="newsroom-picker"
      className="font-mono text-[var(--text)]"
      style={{
        background: "rgba(4, 5, 10, 0.85)",
        border:     "1px solid rgba(255, 46, 99, 0.30)",
        boxShadow:  "0 0 24px rgba(255, 46, 99, 0.10) inset",
        backdropFilter: "blur(12px)",
        padding:    compact ? "12px" : "20px 22px",
        minWidth:   compact ? 280 : 360,
      }}
    >
      <div
        className="flex items-center justify-between mb-3"
        style={{ borderBottom: "1px solid rgba(255,46,99,0.25)", paddingBottom: 8 }}
      >
        <div className="flex items-center gap-2">
          <span className="text-[10px] uppercase tracking-[0.30em] text-glow-red">Federation</span>
          <span className="text-[10px] uppercase tracking-[0.22em] text-[var(--text-3)]">Newsroom</span>
        </div>
        <button
          type="button"
          onClick={load}
          data-testid="newsroom-refresh"
          className="text-[10px] uppercase tracking-[0.20em] text-[var(--text-3)] hover:text-glow-cyan transition-colors"
          aria-label="Refresh newsroom"
        >
          ↻
        </button>
      </div>

      {loading && (
        <div className="text-[11px] text-[var(--text-3)] py-4 text-center" data-testid="newsroom-loading">
          loading…
        </div>
      )}
      {err && !loading && (
        <div className="text-[11px] text-glow-red py-4 text-center" data-testid="newsroom-error">
          {err}
        </div>
      )}

      {/* ── Press outreach pre-fills · Architect mandate Feb 2026 ── */}
      <div
        className="mb-3 pb-3"
        style={{ borderBottom: "1px solid rgba(255,46,99,0.18)" }}
        data-testid="newsroom-press-outreach"
      >
        <div className="text-[9px] uppercase tracking-[0.28em] text-[var(--text-3)] mb-2">
          ◆ Press · One-tap outreach
        </div>
        <div className={`grid ${compact ? "grid-cols-3" : "grid-cols-3"} gap-2`}>
          {NEWSROOM_OUTREACH.map((o) => (
            <a
              key={o.id}
              href={buildMailto(o.email, o.label)}
              data-testid={`newsroom-outreach-${o.id}`}
              title={`Email ${o.label} (${o.email}) with the Federation pitch pre-filled`}
              className="text-center px-2 py-2 text-[10px] uppercase tracking-[0.18em] transition-transform duration-200 hover:scale-[1.04]"
              style={{
                color:      o.color,
                background: "rgba(4,5,10,0.65)",
                border:     `1px solid ${o.color}55`,
                boxShadow:  `0 0 10px ${o.color}25`,
                textShadow: `0 0 6px ${o.color}66`,
                fontWeight: 600,
                textDecoration: "none",
              }}
            >
              ✉ {o.name}
            </a>
          ))}
        </div>
      </div>

      <ul className="space-y-2">
        {items.map((it) => {
          const meta = KIND_META[it.kind] || { icon: "·", label: it.kind.toUpperCase(), color: "#9ca3af" };
          const interactive = typeof onPick === "function";
          const Tag = interactive ? "button" : "div";
          return (
            <Tag
              key={it.id}
              type={interactive ? "button" : undefined}
              onClick={interactive ? () => onPick(it) : undefined}
              data-testid={`newsroom-item-${it.id}`}
              className={`w-full text-left flex items-start gap-2 px-2 py-1.5 transition-colors ${interactive ? "hover:bg-[rgba(255,46,99,0.06)] cursor-pointer" : ""}`}
              style={{ borderLeft: `2px solid ${meta.color}` }}
            >
              <span
                aria-hidden
                className="font-display text-base shrink-0 leading-none mt-0.5"
                style={{ color: meta.color, textShadow: `0 0 6px ${meta.color}` }}
              >
                {meta.icon}
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex items-baseline gap-2">
                  <span
                    className="text-[9px] uppercase tracking-[0.20em] shrink-0"
                    style={{ color: meta.color }}
                  >
                    {meta.label}
                  </span>
                  <span className="text-[9px] text-[var(--text-3)] ml-auto shrink-0">{relTime(it.timestamp)}</span>
                </div>
                <div className={`text-[${compact ? "11" : "12"}px] text-[var(--text)] truncate`}>
                  {it.summary}
                </div>
              </div>
            </Tag>
          );
        })}
        {!loading && !err && items.length === 0 && (
          <li className="text-[11px] text-[var(--text-3)] py-4 text-center">No federation events yet.</li>
        )}
      </ul>

      <div
        className="mt-3 pt-2 text-[9px] uppercase tracking-[0.18em] text-[var(--text-3)] text-center"
        style={{ borderTop: "1px solid rgba(255,46,99,0.18)" }}
      >
        nextxus.online · 200-Year Mandate
      </div>
    </div>
  );
}
